import { ChangeEvent, useCallback, useRef, useState } from "react";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { addUserMessage } from "../redux/slice";
import { hiddenSecurityPart } from "../../../shared/utils/hiddenSecurityPart";
import { getFileExtension } from "../../Meeting/helpers/getFileExtension";
import {
  FILE_CHAT_META_INSTRUCTION,
  UPLOADED_FILE_EXTENSIONS,
} from "../../../common/Chat/constants";
import { getArtifactContentAction } from "../../Files/redux/actions";
import { getFileConfig } from "../../Files/helpers";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { selectSpaValue } from "../redux/selectors";
import {
  deleteEditorThreadAction,
  postMessageWithResponsesAction,
  uploadFileToEditorDialogAction,
} from "../redux/actions";
import { selectIsFileLoading } from "../../Assistants/redux/selectors";
import { createNotification } from "../../../app/state/notificationSlice/slice";

export const useEditorChat = () => {
  const dispatch = useAppDispatch();
  const value = useAppSelector(selectSpaValue);
  const { selectedId, selectedObject } = useStorageSelectors("ai_agents_spa");
  const [message, setMessage] = useState<string>("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>("");
  const isFileLoading = useAppSelector(selectIsFileLoading);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const uploadAbortFnRef = useRef<(() => void) | null>(null);

  const handleChangeMessage = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  };

  const handleFileUpload = (e: any) => {
    if (!e.target.files[0]) return;
    const file = e.target.files[0];
    setFileName(file.name);

    const fileExtension = getFileExtension(file.name) ?? "";
    if (!UPLOADED_FILE_EXTENSIONS.includes(fileExtension)) {
      dispatch(
        createNotification(
          "warning",
          `Расширение загруженного файла (${fileExtension}) не поддерживается`,
        ),
      );
      setFileName(null);
      setFileContent("");
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      return;
    }

    const promise = dispatch(uploadFileToEditorDialogAction(file));
    const abortFn = () => promise.abort();
    uploadAbortFnRef.current = abortFn;

    promise
      .unwrap()
      .then((text) => {
        setFileContent(text);
      })
      .catch((error) => {
        if (error !== "Aborted") {
          console.error(error);
        }
      });
  };

  const handleUploadHtml = () => {
    if (!value) return;
    const name = `${selectedObject?.friendly_name}.html`;
    setFileName(name);
    setFileContent(value?.spa_object?.html_content);
  };

  const handleClick = useCallback(
    async (hash: string, fileName: string) => {
      const fileConfig = getFileConfig(fileName);
      if (!fileConfig) return;
      setFileName(fileName);
      const fileExtension = getFileExtension(fileName) ?? "";
      if (!UPLOADED_FILE_EXTENSIONS.includes(fileExtension)) {
        dispatch(
          createNotification(
            "warning",
            `Расширение загруженного файла (${fileExtension}) не поддерживается`,
          ),
        );
        setFileName(null);
        setFileContent("");
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        return;
      }

      const promise = dispatch(getArtifactContentAction({ hash, fileName }));
      const abortFn = () => promise.abort();
      uploadAbortFnRef.current = abortFn;

      promise
        .unwrap()
        .then((content) => {
          setFileContent(content);
        })
        .catch((error) => {
          if (error !== "Aborted") {
            console.error(error);
          }
        });
    },
    [fileName, fileContent],
  );

  const handleSendMessage = useCallback(() => {
    if (!value?.spa_object) {
      return;
    }
    const finalMessage =
      fileName !== ""
        ? `${message} <security> ${FILE_CHAT_META_INSTRUCTION} ${fileContent}</security> \n\n<file>${fileName || fileName}</file>`
        : message;
    dispatch(addUserMessage(hiddenSecurityPart(finalMessage)));
    dispatch(
      postMessageWithResponsesAction({
        message: finalMessage,
        id: selectedId ?? "",
      }),
    );
    setMessage("");
    setFileName(null);
    setFileContent("");
    setFileName("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [message, fileName, fileContent, dispatch]);

  const handleSendKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.ctrlKey && event.key === "Enter") {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [message, fileName, fileContent, dispatch],
  );

  const handleClearChat = useCallback(() => {
    if (!value?.spa_object || !value.spa_object?.thread_id) return;
    dispatch(deleteEditorThreadAction(value.spa_object.thread_id));
  }, [value?.spa_object]);

  const handleDeleteFileTag = () => {
    uploadAbortFnRef.current?.();
    setFileName(null);
    setFileContent("");
    setFileName("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return {
    message,
    inputRef,
    isFileLoading,
    fileName,
    handleClick,
    handleClearChat,
    handleUploadHtml,
    handleDeleteFileTag,
    handleFileUpload,
    handleChangeMessage,
    handleSendKeyDown,
    handleSendMessage,
  };
};
